import { create } from 'zustand';
import { apiClient } from '../api/config';
import { endpoints } from '../api/endpoints';
import { notificationService } from '../api/services/notificationService';

const matchesFilters = (item, filters) => (
  (filters.read === 'all' || item.read === (filters.read === 'read')) &&
  (filters.category === 'all' || item.category === filters.category)
);

const updateItem = (items, id, changes) => items.map(item => item.id === id ? { ...item, ...changes } : item);

const connectEventStream = async ({ url, token, signal, onOpen, onMessage }) => {
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
    signal,
    cache: 'no-store'
  });
  if (!response.ok || !response.body) throw new Error(`Notification stream failed with ${response.status}`);
  onOpen();

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (!signal.aborted) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
    let boundary = buffer.indexOf('\n\n');
    while (boundary >= 0) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      if (block && !block.startsWith(':')) {
        let event = 'message';
        const data = [];
        for (const line of block.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
        }
        onMessage({ event, data: data.join('\n') });
      }
      boundary = buffer.indexOf('\n\n');
    }
  }
  if (!signal.aborted) throw new Error('Notification stream closed');
};

const useNotificationStore = create((set, get) => ({
  recentItems: [],
  items: [],
  unreadCount: 0,
  nextCursor: null,
  filters: { read: 'all', category: 'all' },
  loadingRecent: false,
  loadingPage: false,
  error: null,
  streamStatus: 'disconnected',
  streamController: null,
  lastIncoming: null,

  fetchRecent: async () => {
    set({ loadingRecent: true, error: null });
    try {
      const result = await notificationService.getNotifications({ limit: 8 });
      set({ recentItems: result.items, unreadCount: result.unreadCount, loadingRecent: false });
      return result;
    } catch (error) {
      set({ loadingRecent: false, error: error.response?.data?.message || 'Unable to load notifications' });
      throw error;
    }
  },

  fetchPage: async ({ append = false } = {}) => {
    const { filters, nextCursor } = get();
    set({ loadingPage: true, error: null });
    try {
      const result = await notificationService.getNotifications({
        limit: 20,
        ...(append && nextCursor ? { cursor: nextCursor } : {}),
        ...(filters.read === 'all' ? {} : { read: filters.read === 'read' }),
        ...(filters.category === 'all' ? {} : { category: filters.category })
      });
      set(state => ({
        items: append ? [...state.items, ...result.items.filter(item => !state.items.some(existing => existing.id === item.id))] : result.items,
        nextCursor: result.nextCursor,
        unreadCount: result.unreadCount,
        loadingPage: false
      }));
      return result;
    } catch (error) {
      set({ loadingPage: false, error: error.response?.data?.message || 'Unable to load notifications' });
      throw error;
    }
  },

  setFilters: (filters) => set(state => ({ filters: { ...state.filters, ...filters }, nextCursor: null })),

  setReadState: async (id, read) => {
    const previousRecent = get().recentItems;
    const previousItems = get().items;
    const previousUnread = get().unreadCount;
    const changedItem = [...previousRecent, ...previousItems].find(item => item.id === id);
    const unreadDelta = changedItem && changedItem.read !== read ? (read ? -1 : 1) : 0;
    const changes = { read, readAt: read ? new Date().toISOString() : null };
    set({
      recentItems: updateItem(previousRecent, id, changes),
      items: updateItem(previousItems, id, changes),
      unreadCount: Math.max(0, previousUnread + unreadDelta)
    });
    try {
      await notificationService.setReadState(id, read);
    } catch (error) {
      set({ recentItems: previousRecent, items: previousItems, unreadCount: previousUnread });
      throw error;
    }
  },

  markAllRead: async () => {
    const previousRecent = get().recentItems;
    const previousItems = get().items;
    const previousUnread = get().unreadCount;
    set({
      recentItems: previousRecent.map(item => ({ ...item, read: true, readAt: new Date().toISOString() })),
      items: previousItems.map(item => ({ ...item, read: true, readAt: new Date().toISOString() })),
      unreadCount: 0
    });
    try {
      await notificationService.markAllRead();
    } catch (error) {
      set({ recentItems: previousRecent, items: previousItems, unreadCount: previousUnread });
      throw error;
    }
  },

  receiveNotification: (item) => set(state => {
    const alreadyKnown = state.recentItems.some(existing => existing.id === item.id) || state.items.some(existing => existing.id === item.id);
    if (alreadyKnown) return state;
    return {
      recentItems: [item, ...state.recentItems].slice(0, 8),
      items: matchesFilters(item, state.filters) ? [item, ...state.items] : state.items,
      unreadCount: state.unreadCount + (item.read ? 0 : 1),
      lastIncoming: item
    };
  }),

  connectStream: () => {
    get().streamController?.abort();
    const token = localStorage.getItem('jms_token');
    if (!token) return;
    const controller = new AbortController();
    set({ streamController: controller, streamStatus: 'connecting' });

    connectEventStream({
      url: `${apiClient.defaults.baseURL}${endpoints.notifications.stream}`,
      token,
      signal: controller.signal,
      onOpen: () => {
        set({ streamStatus: 'connected' });
        get().fetchRecent().catch(() => {});
      },
      onMessage: event => {
        if (event.event === 'notification' && event.data) {
          get().receiveNotification(JSON.parse(event.data));
        }
      }
    }).catch(() => {
      if (!controller.signal.aborted) {
        set({ streamStatus: 'disconnected' });
        setTimeout(() => {
          if (get().streamController === controller) get().connectStream();
        }, 5000);
      }
    });
  },

  disconnectStream: () => {
    get().streamController?.abort();
    set({ streamController: null, streamStatus: 'disconnected' });
  },

  clearLastIncoming: () => set({ lastIncoming: null }),
  reset: () => {
    get().disconnectStream();
    set({ recentItems: [], items: [], unreadCount: 0, nextCursor: null, error: null, lastIncoming: null });
  }
}));

export default useNotificationStore;
